// The app's frame, as today's (App.tsx there): the header, the page the
// address names, the toasts. In the Mac app's window the workspace
// (workspace/) holds the tabs and both panes, each tab a frame of its own
// under the workspace's header and row of tabs; in a browser the one frame
// follows the browser's address.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link, MemoryRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { BoardPage } from './board/BoardPage'
import { CodePage } from './code/CodePage'
import { Header } from './components/Header'
import { BoardLogo, BoardWordmark } from './components/Icons'
import { ErrorState, Loading, NotFound, PageBoundary } from './components/layout'
import { clearToasts, Toaster } from './components/toasts'
import { UnsavedChangesGuard } from './components/unsaved'
import { useScrollMemory } from './components/useScrollMemory'
import { IssuePage } from './issue/IssuePage'
import { DocsPage } from './docs/DocsPage'
import { OldLinkPage } from './links/OldLink'
import { PlanPage } from './plans/PlanPage'
import { PlansPage } from './plans/PlansPage'
import { inMacShell, tellShell } from './lib/shell'
import { isSheet } from './lib/keys'
import { MacNotifications, MacStatus } from './mac/Bridge'
import { EmailLinkPage } from './people/EmailSignIn'
import { SignInPage } from './people/SignInPage'
import { ProjectRoute } from './projects/ProjectRoute'
import { boardForOld, sessionPath } from './projects/routing'
import { SettingsPage } from './settings/SettingsPage'
import { settingsPath } from './settings/routing'
import { SearchPalette } from './search/SearchPalette'
import { MacPresence } from './push/Presence'
import { useBoardState, useStore } from './store/context'
import { canInteract, SurfaceContext, useTabOwner, useTabScope } from './workspace/context'

/**
 * Signing in, or the app: the workspace in the Mac app's window, or one frame.
 * While nobody is signed in, the workspace is not drawn and the shell is told
 * so: what it holds belongs to whoever signs in next.
 */
export function App({ workspace, clearWorkspaceRecovery }: { workspace?: ReactNode; clearWorkspaceRecovery?: () => void }) {
  const { status, me } = useBoardState()
  // What was said to one person is not for the next: a sign-out, a session that ended, another tab's sign-in.
  const who = me && `${me.workspace.id}/${me.member?.id ?? ''}`
  useLayoutEffect(() => clearToasts(), [who])
  const signedOut = status === 'signed_out'
  useEffect(() => {
    if (!workspace || !signedOut) return
    tellShell({ workspaceUnavailable: true })
    clearWorkspaceRecovery?.()
  }, [workspace, signedOut, clearWorkspaceRecovery])
  return (
    <>
      <MacNotifications /><MacPresence />
      <MacStatus />
      {signedOut ? (workspace ? <MemoryRouter><SignedOut /></MemoryRouter> : <SignedOut />) : (workspace ?? <Frame />)}
    </>
  )
}

function SignedOut() {
  return (
    <div className="app">
      <header className="app-header" data-drag-region>
        <Link className="brand" to="/" aria-label="Poulpe">
          <BoardLogo size={20} />
          <span className="brand-name">
            <BoardWordmark height={18} />
          </span>
        </Link>
      </header>
      <main className="app-main">
        <SignInPage />
      </main>
      <Toaster />
    </div>
  )
}

/** A tab's frame: the header (the workspace draws its own over each pane), the page the address names, the toasts. */
export function Frame({ workspace = false }: { workspace?: boolean }) {
  const tabScope = useTabScope()
  const owner = useTabOwner()
  const store = useStore()
  const { status, error, selectedProjectId, projects, proposals } = useBoardState()
  const location = useLocation()
  const { pathname } = location
  const navigate = useNavigate()
  const settingsOpen = isSheet(pathname)
  const [searching, setSearching] = useState(false)
  useLayoutEffect(() => { if (settingsOpen || !owner) setSearching(false) }, [settingsOpen, owner])
  const background = useRef(location)
  const redirecting = pathname === '/runners' || pathname === '/new-project'
  if (!settingsOpen && !redirecting) background.current = location
  let underSheet = settingsOpen ? (isSheet(background.current.pathname) || ['/runners', '/new-project'].includes(background.current.pathname)
    ? { ...location, pathname: '/', search: '', hash: '' } : background.current) : location
  if (settingsOpen && tabScope) {
    const under = new URL(tabScope.background(), 'http://workspace.invalid')
    underSheet = { ...underSheet, pathname: under.pathname, search: under.search, hash: under.hash }
  }
  // Nothing under a sheet may redirect, or the sheet would close: `/` there is drawn as the Board it leads to.
  const home = settingsOpen ? boardForOld(underSheet.pathname, underSheet.hash, [...projects.values()], selectedProjectId, true, [...proposals.values()]) : null
  if (home) {
    const at = new URL(home, 'http://app.invalid')
    underSheet = { ...underSheet, pathname: at.pathname, search: at.search, hash: at.hash }
  }
  const appSurface = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (settingsOpen) appSurface.current?.setAttribute('inert', '')
    else appSurface.current?.removeAttribute('inert')
  }, [settingsOpen])
  const shortcutSettings = selectedProjectId ? settingsPath('project', 'repository', selectedProjectId) : settingsPath('workspace', 'projects')
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (!canInteract(tabScope)) return
      if (!inMacShell() && event.metaKey && !event.altKey && event.key === ',') {
        event.preventDefault()
        if (!settingsOpen) navigate(shortcutSettings)
      } else if (!settingsOpen && status === 'ready' && (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearching((open) => !open)
      }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [navigate, settingsOpen, shortcutSettings, tabScope, status])
  useEffect(() => {
    if (!workspace) return
    const open = (event: Event) => {
      if ((event as CustomEvent<unknown>).detail === tabScope?.id && canInteract(tabScope) && !settingsOpen && status === 'ready') setSearching(true)
    }
    window.addEventListener('poulpe-workspace-search', open)
    return () => window.removeEventListener('poulpe-workspace-search', open)
  }, [workspace, tabScope, settingsOpen, status])
  const main = useRef<HTMLElement>(null)
  useScrollMemory(main)
  let body
  if (status === 'loading') body = <Loading />
  else if (status === 'error') body = <ErrorState error={error} onRetry={() => void store.load()} />
  else
    body = (
      <PageBoundary resetKey={pathname} onReload={tabScope?.reload}>
        {/* Static segments outrank `/:key`, so `/board` and `/queue` never read as issue keys. */}
        <Routes location={underSheet}>
          {/* Needs you is gone (Poulpe-406): `/` is the Board, and its old addresses and the queue's are its Ready to merge, which a link to one proposal (`#proposal-<id>`) still reaches. */}
          {['/', '/needs-you', '/queue', '/p/:project/queue'].map((path) => <Route key={path} path={path} element={<OldAddress />} />)}
          {['/board', '/p/:project/board'].map((path) => <Route key={path} path={path} element={<ProjectRoute><BoardPage /></ProjectRoute>} />)}
          {/* One element for every Code address, so moving between sessions keeps the page. */}
          {['/code', '/code/:id', '/p/:project/code', '/p/:project/code/:id'].map((path) => <Route key={path} path={path} element={<ProjectRoute><CodePage /></ProjectRoute>} />)}
          {['/plans', '/p/:project/plans'].map((path) => <Route key={path} path={path} element={<ProjectRoute><PlansPage /></ProjectRoute>} />)}
          {['/plan/:id', '/p/:project/plan/:id'].map((path) => <Route key={path} path={path} element={<ProjectRoute><PlanPage /></ProjectRoute>} />)}
          {/* Today's link paths (links/oldLinks.ts): a plan's page in its project, a document's repository path, the issue a request was about, today's names of pages. */}
          {['/files/:sha256', '/doc/:id', '/r/:id', '/run/:id', '/issue', '/issue/:ref', '/link/:ref', '/projects', '/now', '/agents'].map((path) => <Route key={path} path={path} element={<OldLinkPage />} />)}
          {['/docs/*', '/p/:project/docs/*'].map(path => <Route key={path} path={path} element={<ProjectRoute><DocsPage /></ProjectRoute>} />)}
          <Route path="/sessions/:id" element={<SessionRoute />} />
          <Route path="/signin/:attempt" element={<EmailLinkPage />} />
          <Route path="/new-project" element={<Navigate to={settingsPath('workspace', 'projects')} replace />} />
          <Route path="/runners" element={<Navigate to={settingsPath('workspace', 'runners')} replace />} />
          <Route path="/:key" element={<IssuePage />} />
          <Route path="*" element={<NotFound what="page" />} />
        </Routes>
      </PageBoundary>
    )
  return (
    <div className="app">
      <div className="app-surface" ref={appSurface}>
      {!workspace && <Header onSearch={() => { if (status === 'ready' && !settingsOpen) setSearching(true) }} />}
      <main className="app-main" ref={main}>
        <SurfaceContext.Provider value={underSheet.pathname}>{body}</SurfaceContext.Provider>
      </main>
      </div>
      <SurfaceContext.Provider value={pathname}>
        {settingsOpen && status === 'ready' && <Routes>
          <Route path="/settings/*" element={<SettingsPage onClose={() => navigate(`${underSheet.pathname}${underSheet.search}${underSheet.hash}`)} />} />
          <Route path="/setup" element={<Navigate to="/settings" replace />} />
        </Routes>}
      </SurfaceContext.Provider>
      {searching && owner && !settingsOpen && status === 'ready' && <SearchPalette onClose={() => setSearching(false)} />}
      {!workspace && <Toaster />}
      <UnsavedChangesGuard under={underSheet.pathname} />
    </div>
  )
}

/** `/`, or an old address of Needs you or the queue, saved or linked: the selected project's Board, keeping the proposal it names. */
function OldAddress() {
  const { pathname, hash } = useLocation()
  const { projects, selectedProjectId, proposals } = useBoardState()
  return <Navigate to={boardForOld(pathname, hash, [...projects.values()], selectedProjectId, true, [...proposals.values()]) ?? '/board'} replace />
}

/** A session's address that names no project (a notification's, an older link): its project's Code tab. */
function SessionRoute() {
  const { id = '' } = useParams()
  const { sessions, projects } = useBoardState()
  const project = projects.get(sessions.get(id)?.project_id ?? '')
  return project ? <Navigate to={sessionPath(project.key, id)} replace /> : <NotFound what="session" />
}
